import { Button, Drawer, Group, Loader, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconCalendarWeek,
  IconChartBar,
  IconChevronRight,
  IconClock,
  IconClockHour4,
  IconMessages,
  IconMicrophone,
  IconPhone,
  IconPhoneCall,
  IconRefresh,
  IconHeadset,
  IconRobot,
  IconUser,
} from '@tabler/icons-react';
import { md5 } from 'js-md5';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRealtimeUsage, RealtimeUsageSession, queryMessageDetail } from '@mygpt/app';

const CARD_SHADOW = '0px 10px 60px 0px rgba(226, 236, 249, 0.50)';
const VIOLET = '#7C68FB';
const VIOLET_SOFT = 'rgba(124, 104, 251, 0.10)';

interface AudioSegment {
  segment_index: number;
  url: string;
}

interface TranscriptTurn {
  role: 'user' | 'assistant';
  content: string;
  // 同传通话（question_metadata.interpreter）：该发言的原文语言与译文。
  // 气泡内原文在上、译文在下（无译文只显示原文）；非同传通话为 undefined → 单段气泡、行为不变
  interp?: { lang?: string; translated_text?: string; translated_lang?: string } | null;
}

const formatDuration = (ms: number) => {
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const hm = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return isToday ? hm : `${d.toLocaleDateString([], { month: 'numeric', day: 'numeric' })} ${hm}`;
};

export const CallLog = ({ botId, phoneNumber }: { botId: string; phoneNumber?: string }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<RealtimeUsageSession[]>([]);
  // 通话详情抽屉（录音 + 履历合一）
  const [detail, setDetail] = useState<RealtimeUsageSession | null>(null);
  const [segments, setSegments] = useState<AudioSegment[]>([]);
  const [transcript, setTranscript] = useState<TranscriptTurn[]>([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  const load = async () => {
    if (!botId) return;
    try {
      setLoading(true);
      const start = new Date();
      start.setDate(start.getDate() - 30);
      const { data } = await getRealtimeUsage(botId, { start_time: start.toISOString() });
      // 最新在前
      setSessions([...(data.sessions || [])].reverse());
    } catch (error) {
      console.error('加载着信ログ失败:', error);
      showNotification({ message: t('phone.log.load_error'), color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [botId]);

  // 打开通话详情抽屉：并行拉录音 presigned URL + 聊天履历
  const openDetail = (s: RealtimeUsageSession) => {
    setDetail(s);
    setSegments([]);
    setTranscript([]);

    // 录音（md5 master 鉴权，与录音上传同款）
    setAudioLoading(true);
    const authHash = md5(`master:${botId}`);
    const apiHost = import.meta.env.VITE_API_BASE_URL;
    fetch(`${apiHost}/v1/robots/${botId}/realtime-audio/download?session_id=${encodeURIComponent(s.session_id)}`, {
      headers: { Authorization: `Bearer ${authHash}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => setSegments(data.segments || []))
      .catch((e) => console.error('获取录音失败:', e))
      .finally(() => setAudioLoading(false));

    // 聊天履历（admin JWT，session.messages 按 session_id 取）
    setTranscriptLoading(true);
    queryMessageDetail({ aiId: botId, sessionId: s.session_id, endTime: new Date().toISOString(), page: 1, size: 200 })
      .then(({ data }: any) => {
        const items = data?.results || data?.items || data?.data || [];
        const turns: TranscriptTurn[] = [];
        for (const m of items) {
          // 同传通话：question_metadata.interpreter = {question:{lang,translated_text,...}, answer:{...}}
          const interp = m.question_metadata?.interpreter;
          if (m.question) turns.push({ role: 'user', content: m.question, interp: interp?.question ?? null });
          if (m.answer) turns.push({ role: 'assistant', content: m.answer, interp: interp?.answer ?? null });
        }
        setTranscript(turns);
      })
      .catch((e) => console.error('获取履历失败:', e))
      .finally(() => setTranscriptLoading(false));
  };

  // 同传通话（履历带 interpreter 元数据）：录音两段各有含义，用说明标签替代泛用的「セグメント N」
  //   seg 1 = 原声（左=お客様/右=オペレーター，定责/转写用）
  //   seg 2 = 体験ミックス（左=お客様側/右=オペレーター側，含各自听到的译文，戴耳机评估同传效果）
  const isInterpCall = transcript.some((tt) => !!tt.interp);

  const today = new Date().toDateString();
  const week = new Date();
  week.setDate(week.getDate() - 7);
  const todayCalls = sessions.filter((s) => new Date(s.created_at).toDateString() === today).length;
  const weekCalls = sessions.filter((s) => new Date(s.created_at) >= week).length;
  const durations = sessions.map((s) => s.duration_ms).filter((d) => d > 0);
  const avgDuration = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;

  const stats = [
    {
      label: t('phone.log.stat_today'),
      value: String(todayCalls),
      Icon: IconPhoneCall,
      color: VIOLET,
      soft: VIOLET_SOFT,
    },
    {
      label: t('phone.log.stat_week'),
      value: String(weekCalls),
      Icon: IconCalendarWeek,
      color: '#12B886',
      soft: 'rgba(18,184,134,0.10)',
    },
    {
      label: t('phone.log.stat_avg_duration'),
      value: avgDuration ? formatDuration(avgDuration) : '—',
      Icon: IconClock,
      color: '#FD7E14',
      soft: 'rgba(253,126,20,0.10)',
    },
    {
      label: t('phone.log.stat_total'),
      value: String(sessions.length),
      Icon: IconChartBar,
      color: '#868E96',
      soft: 'rgba(134,142,150,0.12)',
    },
  ];

  return (
    <Stack gap={16}>
      {/* 标题行 */}
      <Group justify="space-between" align="flex-start">
        <div>
          <Text fz={16} fw={700}>
            {t('phone.log.title')}
          </Text>
          <Text fz={12.5} c="gray.6" mt={2}>
            {phoneNumber ? t('phone.log.subtitle', { number: phoneNumber }) : t('phone.log.subtitle_generic')}
          </Text>
        </div>
        <Button variant="default" size="xs" leftSection={<IconRefresh size={14} />} onClick={load} loading={loading}>
          {t('phone.log.refresh')}
        </Button>
      </Group>

      {/* 统计卡 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        {stats.map(({ label, value, Icon, color, soft }) => (
          <div
            key={label}
            className="rounded-xl bg-white"
            style={{ boxShadow: CARD_SHADOW, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: soft,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon size={21} color={color} />
            </div>
            <div style={{ minWidth: 0 }}>
              <Text fz={11.5} c="gray.6" truncate>
                {label}
              </Text>
              <Text fz={23} fw={700} lh={1.15} mt={2}>
                {value}
              </Text>
            </div>
          </div>
        ))}
      </div>

      {/* 通话列表 */}
      <div className="rounded-xl bg-white" style={{ boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '11px 20px',
            borderBottom: '1px solid #ECECF1',
            background: 'linear-gradient(180deg, #FBFBFD, #F6F7FA)',
            fontSize: 11,
            fontWeight: 700,
            color: '#9AA0AC',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          <div style={{ flex: '0 0 210px' }}>{t('phone.log.col_caller')}</div>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>{t('phone.log.col_topic')}</div>
          <div style={{ flex: '0 0 124px' }}>{t('phone.log.col_time')}</div>
          <div style={{ flex: '0 0 92px' }}>{t('phone.log.col_duration')}</div>
          <div style={{ flex: '0 0 80px' }}>{t('phone.log.col_turns')}</div>
          <div style={{ flex: '0 0 44px' }} />
        </div>

        {loading && (
          <Group justify="center" py={40}>
            <Loader size="sm" />
          </Group>
        )}
        {!loading && sessions.length === 0 && (
          <Stack align="center" py={48} gap={8}>
            <IconPhone size={28} color="#C0C4CC" />
            <Text size="sm" c="gray.6">
              {t('phone.log.empty')}
            </Text>
          </Stack>
        )}
        {!loading &&
          sessions.slice(0, 50).map((s, i) => (
            <div
              key={s.session_id}
              onClick={() => openDetail(s)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '14px 20px',
                borderBottom: i < Math.min(sessions.length, 50) - 1 ? '1px solid #F2F3F7' : 'none',
                fontSize: 13.5,
                cursor: 'pointer',
                transition: 'background 0.12s, box-shadow 0.12s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FAFAFE';
                e.currentTarget.style.boxShadow = `inset 3px 0 0 ${VIOLET}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              {/* 発信元（圆底电话图标 + 号码）。语言徽章暂隐藏：bridge 落库的 language
                  当前写死 ja，不准；待 bridge 改为按实际通话语言落库后再放出（详情抽屉同理）。 */}
              <div
                style={{
                  flex: '0 0 210px',
                  minWidth: 0,
                  paddingRight: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: VIOLET_SOFT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconPhone size={16} color={VIOLET} />
                </div>
                <Text fw={600} fz={13.5} truncate style={{ fontFamily: 'monospace' }}>
                  {s.user_identifier || t('phone.log.anonymous')}
                </Text>
              </div>
              {/* 用件（独立列，填满中间空间） */}
              <div style={{ flex: '1 1 auto', minWidth: 0, paddingRight: 16 }}>
                <Text fz={13} c={s.title ? 'gray.7' : 'gray.5'} fs={s.title ? undefined : 'italic'} truncate>
                  {s.title || t('phone.log.no_summary')}
                </Text>
              </div>
              <div style={{ flex: '0 0 124px' }}>
                <Group gap={6} wrap="nowrap">
                  <IconClock size={14} color="#ADB5BD" style={{ flexShrink: 0 }} />
                  <Text fz={12.5} c="gray.7" truncate>
                    {formatTime(s.created_at)}
                  </Text>
                </Group>
              </div>
              <div style={{ flex: '0 0 92px' }}>
                <Group gap={6} wrap="nowrap">
                  <IconClockHour4 size={14} color="#ADB5BD" style={{ flexShrink: 0 }} />
                  <Text fz={12.5} c={s.duration_ms ? 'gray.7' : 'gray.5'} style={{ fontFamily: 'monospace' }}>
                    {s.duration_ms ? formatDuration(s.duration_ms) : '—'}
                  </Text>
                </Group>
              </div>
              <div style={{ flex: '0 0 80px' }}>
                <Group gap={6} wrap="nowrap">
                  <IconMessages size={14} color="#ADB5BD" style={{ flexShrink: 0 }} />
                  <Text fz={12.5} c="gray.7" fw={500}>
                    {s.turn_count}
                  </Text>
                </Group>
              </div>
              <div
                style={{
                  flex: '0 0 44px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <IconChevronRight size={16} color="#C5C9D3" />
              </div>
            </div>
          ))}
      </div>

      {/* 通话详情抽屉：概要 + 录音 + 会話履歴 */}
      <Drawer
        opened={!!detail}
        onClose={() => setDetail(null)}
        position="right"
        size="lg"
        title={
          <Group gap={8}>
            <IconPhone size={17} color={VIOLET} />
            <Text fw={700}>{t('phone.log.detail_title')}</Text>
          </Group>
        }
        styles={{ body: { padding: 0 } }}
      >
        {detail && (
          <Stack gap={0}>
            {/* 概要（基本信息）：圆底图标 + 号码 + 用件 + 图标信息块 */}
            <div
              style={{
                padding: '20px 20px 18px',
                borderBottom: '1px solid #F1F2F6',
                background: `radial-gradient(circle at 0% 0%, ${VIOLET_SOFT}, #fff 60%)`,
              }}
            >
              <Group gap={14} wrap="nowrap" align="flex-start">
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 14,
                    background: VIOLET_SOFT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconPhone size={23} color={VIOLET} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Text fz={19} fw={700} truncate style={{ fontFamily: 'monospace', letterSpacing: 0.3 }}>
                    {detail.user_identifier || t('phone.log.anonymous')}
                  </Text>
                  <Text fz={13} c={detail.title ? 'gray.7' : 'gray.5'} mt={3}>
                    {detail.title || t('phone.log.no_summary')}
                  </Text>
                </div>
              </Group>
              <Group gap={22} mt={16}>
                {[
                  { Icon: IconClock, value: formatTime(detail.created_at) },
                  { Icon: IconClockHour4, value: detail.duration_ms ? formatDuration(detail.duration_ms) : '—' },
                  { Icon: IconMessages, value: String(detail.turn_count) },
                ].map(({ Icon, value }, i) => (
                  <Group key={i} gap={6} wrap="nowrap">
                    <Icon size={15} color="#868E96" />
                    <Text fz={12.5} c="gray.7" fw={500}>
                      {value}
                    </Text>
                  </Group>
                ))}
              </Group>
            </div>

            {/* 録音 */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #F1F2F6' }}>
              <Group gap={7} mb={12}>
                <IconMicrophone size={16} color={VIOLET} />
                <Text fz={13} fw={700} c="gray.8">
                  {t('phone.log.recording_title')}
                </Text>
              </Group>
              {audioLoading ? (
                <Loader size="sm" />
              ) : segments.length === 0 ? (
                <Text fz={12.5} c="gray.5">
                  {t('phone.log.no_audio')}
                </Text>
              ) : (
                <Stack gap={10}>
                  {segments.map((seg) => (
                    <div
                      key={seg.segment_index}
                      style={{
                        background: '#F8F9FB',
                        border: '1px solid #EEF0F4',
                        borderRadius: 12,
                        padding: segments.length > 1 || isInterpCall ? '10px 12px 12px' : 8,
                      }}
                    >
                      {(segments.length > 1 || isInterpCall) && (
                        <Text size="xs" fw={600} c="gray.6" mb={6}>
                          {isInterpCall && seg.segment_index === 0
                            ? t('phone.log.seg_original')
                            : isInterpCall && seg.segment_index === 1
                              ? t('phone.log.seg_review')
                              : `${t('phone.log.segment')} ${seg.segment_index + 1}`}
                        </Text>
                      )}
                      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                      <audio controls src={seg.url} style={{ width: '100%', height: 38 }} />
                    </div>
                  ))}
                </Stack>
              )}
            </div>

            {/* 会話履歴 */}
            <div style={{ padding: '18px 20px' }}>
              <Group gap={7} mb={14} justify="space-between" wrap="nowrap">
                <Group gap={7} wrap="nowrap">
                  <IconMessages size={16} color={VIOLET} />
                  <Text fz={13} fw={700} c="gray.8">
                    {t('phone.log.transcript_title')}
                  </Text>
                </Group>
              </Group>
              {transcriptLoading ? (
                <Loader size="sm" />
              ) : transcript.length === 0 ? (
                <Text fz={12.5} c="gray.5">
                  {t('phone.log.no_transcript')}
                </Text>
              ) : (
                <Stack gap={14}>
                  {transcript.map((turn, idx) => {
                    const isUser = turn.role === 'user';
                    // 同传通话行：气泡内原文（话筒侧实际所说）在上、译文（对侧听到的）在下；
                    // 无译文（相槌/杂音被翻译端点跳过）只显示原文。角标 = 原文语言→译文语言。
                    const isInterp = !!turn.interp;
                    const translated = (turn.interp?.translated_text || '').trim();
                    const srcLang = (turn.interp?.lang || '').toUpperCase();
                    const dstLang = (turn.interp?.translated_lang || '').toUpperCase();
                    const langTag = isInterp ? (translated ? `${srcLang || '?'}→${dstLang || '?'}` : srcLang) : '';
                    return (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          gap: 9,
                          flexDirection: isUser ? 'row' : 'row-reverse',
                          alignItems: 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isUser ? '#EEF0F3' : VIOLET_SOFT,
                          }}
                        >
                          {isUser ? (
                            <IconUser size={16} color="#868E96" />
                          ) : isInterp ? (
                            <IconHeadset size={16} color={VIOLET} />
                          ) : (
                            <IconRobot size={16} color={VIOLET} />
                          )}
                        </div>
                        <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <Text
                            fz={10.5}
                            fw={700}
                            c={isUser ? 'gray.6' : VIOLET}
                            style={{ textAlign: isUser ? 'left' : 'right' }}
                          >
                            {isUser
                              ? t('phone.log.role_caller')
                              : isInterp
                              ? t('phone.log.role_agent')
                              : t('phone.log.role_ai')}
                            {langTag && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  fontWeight: 500,
                                  fontSize: 9.5,
                                  padding: '1px 5px',
                                  borderRadius: 4,
                                  background: translated ? VIOLET_SOFT : '#EEF0F3',
                                  color: translated ? VIOLET : '#868E96',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {langTag}
                              </span>
                            )}
                          </Text>
                          <div
                            style={{
                              padding: '9px 13px',
                              borderRadius: 14,
                              borderTopLeftRadius: isUser ? 4 : 14,
                              borderTopRightRadius: isUser ? 14 : 4,
                              fontSize: 13.5,
                              lineHeight: 1.55,
                              whiteSpace: 'pre-wrap',
                              background: isUser ? '#F4F5F7' : VIOLET_SOFT,
                              color: '#212529',
                            }}
                          >
                            <div>{turn.content}</div>
                            {translated && (
                              <div
                                style={{
                                  marginTop: 6,
                                  paddingTop: 6,
                                  borderTop: '1px dashed rgba(0,0,0,.12)',
                                  fontSize: 12.5,
                                  color: '#5F6B7A',
                                  display: 'flex',
                                  gap: 6,
                                  alignItems: 'baseline',
                                }}
                              >
                                <span
                                  style={{
                                    flexShrink: 0,
                                    fontSize: 9.5,
                                    fontWeight: 600,
                                    padding: '1px 5px',
                                    borderRadius: 4,
                                    background: VIOLET_SOFT,
                                    color: VIOLET,
                                    fontVariantNumeric: 'tabular-nums',
                                    position: 'relative',
                                    top: -1,
                                  }}
                                >
                                  {t('phone.log.translated_tag')}
                                  {dstLang ? ` ${dstLang}` : ''}
                                </span>
                                <span>{translated}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Stack>
              )}
            </div>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
};

export default CallLog;
